import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import {
  setOrderIdToOpen,
  clearOrderIdToOpen,
} from '../store/slices/orderModalSlice';
import { markOrderAsRead } from '../store/slices/orderSlice';
import axios from '../config/axiosConfig';
import { API_ENDPOINTS } from '../config/api';
import OrderDetailModal from './OrderDetailModal';

/**
 * Opens order details from notification tap (popup, push, local notification) on any route.
 * Does not navigate — chef/waiter stay on their dashboards; owner stays wherever they are.
 */
const GlobalOrderDetailModal = () => {
  const orderIdToOpen = useAppSelector((s) => s.orderModal?.orderIdToOpen);
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const [selectedOrder, setSelectedOrder] = useState(null);

  // FCM / cold start: pending id stored before React was ready
  useEffect(() => {
    try {
      const pending = localStorage.getItem('pendingOrderIdFromPush');
      if (pending) {
        localStorage.removeItem('pendingOrderIdFromPush');
        dispatch(setOrderIdToOpen(String(pending)));
      }
    } catch (e) {
      /* ignore */
    }
  }, [dispatch]);

  useEffect(() => {
    if (!orderIdToOpen) return;
    const id = String(orderIdToOpen);
    dispatch(clearOrderIdToOpen());
    setSelectedOrder(null);

    let cancelled = false;
    axios
      .get(API_ENDPOINTS.ORDER_BY_ID(id))
      .then((res) => {
        if (cancelled || !res.data) return;
        setSelectedOrder(res.data);
        try {
          if (localStorage.getItem('pendingOrderIdFromPush') === id) {
            localStorage.removeItem('pendingOrderIdFromPush');
          }
        } catch (e) {
          /* ignore */
        }
        if (user?.role === 'restaurantadmin') {
          axios
            .put(API_ENDPOINTS.ORDER_BY_ID(id), { isRead: true })
            .then(() => dispatch(markOrderAsRead(id)))
            .catch(() => {});
        }
      })
      .catch(() => {
        if (!cancelled) setSelectedOrder(null);
      });

    return () => {
      cancelled = true;
    };
  }, [orderIdToOpen, dispatch, user?.role]);

  const handleClose = () => {
    setSelectedOrder(null);
  };

  if (!selectedOrder) return null;

  return <OrderDetailModal order={selectedOrder} onClose={handleClose} />;
};

export default GlobalOrderDetailModal;
